import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Icons from 'lucide-react';
import type { Notification } from '@/types/notifications';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

interface NotificationBannerProps {
  notification: Notification;
  onDismiss: () => void;
  onClick?: () => void;
  autoDismiss?: boolean;
  duration?: number; // seconds
}

/**
 * Toast-style notification banner that slides in from top-right
 * Features: Auto-dismiss, click to open, glassmorphic design, priority colors
 */
export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  notification,
  onDismiss,
  onClick,
  autoDismiss = true,
  duration = 5,
}) => {
  const [progress, setProgress] = useState(100);

  // Auto-dismiss timer
  useEffect(() => {
    if (!autoDismiss) return;

    const dismissDuration = (notification.auto_dismiss_after || duration) * 1000;
    const interval = 50; // Update every 50ms
    const decrement = (100 / dismissDuration) * interval;

    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - decrement;
        if (newProgress <= 0) {
          onDismiss();
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [autoDismiss, duration, notification.auto_dismiss_after, onDismiss]);

  // Get app icon
  const getAppIcon = () => {
    const iconName = notification.app_icon || 'Bell';
    const IconComponent = (Icons as any)[iconName] || Icons.Bell;
    return <IconComponent className="w-4 h-4" />;
  };

  // Priority color system - McQueen palette: dramatic, monochrome with accent
  const getPriorityStyles = () => {
    switch (notification.priority) {
      case 'urgent':
        return {
          accent: 'from-red-500/30 via-red-500/10 to-transparent',
          glow: 'shadow-[0_0_40px_rgba(220,38,38,0.5),0_0_80px_rgba(220,38,38,0.2)]',
          progress: 'bg-gradient-to-r from-red-600 to-red-500',
          accentBar: 'bg-red-500',
        };
      case 'high':
        return {
          accent: 'from-amber-500/30 via-amber-500/10 to-transparent',
          glow: 'shadow-[0_0_40px_rgba(245,158,11,0.4)]',
          progress: 'bg-gradient-to-r from-amber-600 to-amber-500',
          accentBar: 'bg-amber-500',
        };
      case 'low':
        return {
          accent: 'from-gray-500/20 via-gray-500/5 to-transparent',
          glow: '',
          progress: 'bg-gradient-to-r from-gray-700 to-gray-600',
          accentBar: 'bg-gray-600',
        };
      default:
        return {
          accent: 'from-white/20 via-white/5 to-transparent',
          glow: 'shadow-[0_0_40px_rgba(255,255,255,0.1)]',
          progress: 'bg-gradient-to-r from-white/80 to-white/60',
          accentBar: 'bg-white',
        };
    }
  };

  const priorityStyles = getPriorityStyles();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 400, y: -20 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0, x: 400, scale: 0.95 }}
        transition={{
          type: 'spring',
          stiffness: 200,
          damping: 25,
        }}
        className="cursor-pointer group"
        onClick={() => onClick?.()}
        whileHover={{ scale: 1.01, x: -4 }}
      >
        <div
          className={cn(
            'relative overflow-hidden rounded-xl',
            'backdrop-blur-2xl',
            // Elegant rounded design
            'bg-black/90',
            'border border-white/10',
            'shadow-[0_8px_32px_rgba(0,0,0,0.9)]',
            priorityStyles.glow,
          )}
        >
          {/* McQueen accent bar - sharp vertical line */}
          <div className={cn(
            'absolute left-0 top-0 bottom-0 w-[3px]',
            priorityStyles.accentBar
          )} />
          
          {/* Subtle accent gradient */}
          <div className={cn(
            'absolute inset-0 bg-gradient-to-r',
            priorityStyles.accent,
            'opacity-40 pointer-events-none'
          )} />

          {/* Content */}
          <div className="relative z-10 flex items-start gap-4 p-5 pl-6">
            {/* Avatar or App Icon - McQueen: minimal, sharp */}
            <div className="flex-shrink-0">
              {notification.avatar_url ? (
                <Avatar className="w-12 h-12 rounded-lg border border-white/20">
                  <AvatarImage src={notification.avatar_url} alt="User" />
                  <AvatarFallback className="bg-black border border-white/20 text-white text-sm font-light tracking-widest">
                    {notification.title.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <div className="w-12 h-12 bg-white/5 border border-white/20 rounded-lg flex items-center justify-center text-white/90">
                  {getAppIcon()}
                </div>
              )}
            </div>

            {/* Text Content - McQueen: refined, elegant typography */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h4 className="text-sm font-light tracking-wide text-white/95 leading-snug uppercase">
                  {notification.title}
                </h4>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss();
                  }}
                  className="flex-shrink-0 text-white/40 hover:text-white transition-all duration-300 hover:rotate-90 mt-0.5"
                >
                  <Icons.X className="w-3.5 h-3.5 stroke-[1.5]" />
                </button>
              </div>

              {notification.body && (
                <p className="text-[13px] text-white/70 leading-relaxed line-clamp-2 mb-2 font-light">
                  {notification.body}
                </p>
              )}

              {notification.app_name && (
                <p className="text-[10px] text-white/40 font-light tracking-widest uppercase">
                  {notification.app_name}
                </p>
              )}
            </div>
          </div>

          {/* Progress bar - minimal, architectural */}
          {autoDismiss && (
            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-white/5">
              <motion.div
                className={cn('h-full', priorityStyles.progress)}
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.05, ease: 'linear' }}
              />
            </div>
          )}

          {/* Priority indicator - subtle, elegant pulse */}
          {(notification.priority === 'urgent' || notification.priority === 'high') && (
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className={cn(
                'absolute top-6 right-6 w-1.5 h-1.5',
                notification.priority === 'urgent' ? 'bg-red-500' : 'bg-amber-500'
              )}
              style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}
            />
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Container for managing multiple notification banners
 * Stacks them vertically with proper spacing
 */
interface NotificationBannerStackProps {
  notifications: Notification[];
  onDismiss: (id: string) => void;
  onClick?: (notification: Notification) => void;
  maxVisible?: number;
}

export const NotificationBannerStack: React.FC<NotificationBannerStackProps> = ({
  notifications,
  onDismiss,
  onClick,
  maxVisible = 3,
}) => {
  const visibleNotifications = notifications.slice(0, maxVisible);

  return (
    <div className="fixed top-16 right-6 z-[9999] pointer-events-none w-[380px]">
      <div className="space-y-2 pointer-events-auto">
        <AnimatePresence mode="popLayout">
          {visibleNotifications.map((notification, index) => (
            <motion.div
              key={notification.id}
              layout
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: index * 10 }}
              exit={{ opacity: 0, x: 400 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <NotificationBanner
                notification={notification}
                onDismiss={() => onDismiss(notification.id)}
                onClick={() => onClick?.(notification)}
              />
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Overflow indicator */}
        {notifications.length > maxVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-xs text-white/50 py-2"
          >
            +{notifications.length - maxVisible} more notification{notifications.length - maxVisible !== 1 ? 's' : ''}
          </motion.div>
        )}
      </div>
    </div>
  );
};
